/**
 * Нумерация счетов — `01-BUSINESS-RULES.md` §2.2.
 *
 * Нумерация ГОДОВАЯ: не сквозная и не помесячная. Номер сбрасывается на 1
 * в начале каждого календарного года — по дате СОЗДАНИЯ счёта, не по датам
 * заказов внутри него.
 *
 * Номер редактируется вручную и при создании, и после. Это не косметика, а
 * единственный способ мигрировать нумерацию из прежней программы лаборатории:
 * если последний счёт в старой системе был №367, пользователь ставит 368
 * вручную, и дальше система сама продолжает 369, 370… — потому что следующий
 * номер считается от МАКСИМУМА среди уже созданных, а не от их количества.
 */
import { yearOf } from './dates.js';
/**
 * Следующий номер счёта в году создаваемого документа.
 *
 * Считается как `max(номера того же года) + 1`, а НЕ как `количество + 1`.
 * Разница принципиальная: после ручной установки 368 счёт был один, но
 * следующий обязан быть 369, а не 2.
 */
export function nextInvoiceNumber(invoices, invoiceDate) {
    const year = yearOf(invoiceDate);
    let max = 0;
    for (const invoice of invoices) {
        if (yearOf(invoice.date) === year && invoice.number > max) {
            max = invoice.number;
        }
    }
    return max + 1;
}
/**
 * Занят ли номер в этом году другим счётом.
 *
 * §2.2 разрешает править номер вручную, но два счёта с одним номером в одном
 * году — бухгалтерская ошибка, а не рабочий сценарий. Проверка нужна и на
 * клиенте (сразу подсветить поле), и на сервере (уникальный индекс — последний
 * рубеж, но пользователю нужно понятное сообщение, а не ошибка БД).
 */
export function isNumberTaken(invoices, candidate) {
    const year = yearOf(candidate.date);
    /*
      Приписка входит в сравнение: «368» и «368-1» — разные номера, а вот два
      счёта «368-1» в одном году — та же ошибка, что два просто «368».
      Пустая приписка и её отсутствие — одно и то же состояние.
    */
    const ключ = (n, s) => n + '/' + (s ?? '').trim();
    const свой = ключ(candidate.number, candidate.numberSuffix);
    return invoices.some((i) => i.id !== candidate.id && ключ(i.number, i.numberSuffix) === свой && yearOf(i.date) === year);
}
/** Текст ошибки о занятом номере — одинаковый в интерфейсе и в ответе API. */
export function numberTakenMessage(number, date, suffix) {
    return `Счёт №${invoiceNumberText(number, suffix)} за ${yearOf(date)} год уже существует. Укажите другой номер.`;
}
/**
 * Месяцы заказов, попавших в счёт (§2.4).
 *
 * Объединять заказы разных месяцев разрешено — зуботехническая работа часто
 * выставляется не в том месяце, когда была принята. Условие объединения — только
 * совпадение клиники и юрлица. Но если месяцы разные, интерфейс обязан явно
 * это показать: не заблокировать, а перечислить месяцы, чтобы у пользователя
 * не осталось сомнений, что объединение осознанное.
 */
export function invoiceMonthSpan(orders) {
    const months = [...new Set(orders.map((o) => o.received.slice(0, 7)))].sort();
    return { months, spansMultipleMonths: months.length > 1 };
}
/**
 * Номер счёта для показа и печати: «368» или «368-1».
 *
 * Приписка — исключение, а не правило. Она появилась из ошибки в бумажном
 * учёте: один и тот же номер выдали двум разным клиникам, документы уже у них
 * на руках, и переименовать задним числом нельзя. Программа же справедливо
 * запрещает два счёта с одним номером в году — это бухгалтерская ошибка,
 * и запрет снимать нельзя.
 *
 * Поэтому номер остаётся числом (по нему считается следующий и идёт годовая
 * проверка), а различает такие счета короткая приписка. Автоматически она
 * не выдаётся никогда — только руками, для разбора подобных случаев.
 */
export function invoiceNumberText(number, suffix) {
    const s = (suffix ?? '').trim();
    return s ? `${number}-${s}` : String(number);
}
//# sourceMappingURL=invoice-number.js.map